import { defineStore } from 'pinia';

// Per-tab, so a view-as session survives a refresh and page-to-page navigation but
// never outlives the tab or leaks into another one.
export const VIEW_AS_SESSION_KEY = 'eaViewAsSession';

function readSession() {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSession(session) {
  try {
    if (session) sessionStorage.setItem(VIEW_AS_SESSION_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(VIEW_AS_SESSION_KEY);
  } catch {
    // Storage unavailable — the session lasts for this page load only.
  }
}

export const useViewAsStore = defineStore('viewAs', {
  // Hydrated at creation so a refresh knows whose session to resume.
  state: () => {
    const session = readSession() || {};

    return {
      // The admin who started the session; a session is only ever resumed into their login.
      actorAsurite: session.actorAsurite || '',
      // ASURITE or email — whatever identifier the lookup was made with (ULC-3125).
      targetIdentifier: session.targetIdentifier || '',
      targetName: session.targetName || '',
      // Shown alongside the name so the admin can tell near-identical learners apart.
      // OTP learners have no ASURITE, so this falls back to their email.
      targetAsurite: session.targetAsurite || '',
      targetPartner: session.targetPartner || '',
      // Runtime only, never persisted: true once the target's data has actually been
      // loaded in THIS page load. The banner keys off it so it can never claim a
      // session the portal hasn't switched into yet.
      confirmed: false,
      // Bumped by the banner. SecondaryHeaderComponent owns the actual exit so the
      // banner and the "Stop view as" menu item run the identical teardown.
      exitRequests: 0,
      // Bumped by the banner as the audit token nears expiry. SecondaryHeaderComponent
      // mints the replacement because only it holds the admin's own learner id.
      renewRequests: 0
    };
  },

  getters: {
    isActive: (state) => state.confirmed && !!state.targetIdentifier,
    // A persisted session that this page load hasn't resumed yet.
    hasPendingSession: (state) => !state.confirmed && !!state.targetIdentifier,
    targetLabel: (state) => state.targetName || state.targetIdentifier,
    // ASURITE and partner, in order, with whichever is missing dropped.
    targetDetails: (state) => [state.targetAsurite, state.targetPartner].filter(Boolean)
  },

  actions: {
    start({ actorAsurite = '', targetIdentifier = '', targetName = '', targetAsurite = '', targetPartner = '' } = {}) {
      this.actorAsurite = actorAsurite;
      this.targetIdentifier = targetIdentifier;
      this.targetName = targetName;
      this.targetAsurite = targetAsurite;
      this.targetPartner = targetPartner;
      this.confirmed = !!this.targetIdentifier;
      this.persist();
    },

    stop() {
      this.actorAsurite = '';
      this.targetIdentifier = '';
      this.targetName = '';
      this.targetAsurite = '';
      this.targetPartner = '';
      this.confirmed = false;
      writeSession(null);
    },

    requestExit() {
      this.exitRequests += 1;
    },

    requestRenewal() {
      this.renewRequests += 1;
    },

    persist() {
      if (!this.targetIdentifier) return writeSession(null);

      writeSession({
        actorAsurite: this.actorAsurite,
        targetIdentifier: this.targetIdentifier,
        targetName: this.targetName,
        targetAsurite: this.targetAsurite,
        targetPartner: this.targetPartner
      });
    }
  }
});
